import { useCallback, useEffect, useRef } from "react";
import { useQuery, useApolloClient } from "@apollo/client";
import { useRecoilState, useRecoilValue } from "recoil";
import { useUser } from "MVVM/Hooks/useUser";
import { MY_IN_TRANSIT_CLIPS } from "MVVM/GraphQL/pulse";
import {
  InFlightClipsAtom,
  PollingActiveAtom,
  DismissedTerminalsAtom,
  PulseKickAtom,
} from "MVVM/State/clipStatus";
import type {
  MyInTransitClipsQueryData,
  ClipStatusTimeline,
} from "MVVM/Components/ClipStatus/@types";
import { TERMINAL_STATES } from "MVVM/Components/ClipStatus/@types";

const POLL_INTERVAL = 5000;
const DISMISSED_TTL_MS = 72 * 60 * 60 * 1000;

const isAllTerminal = (clips: ClipStatusTimeline[]): boolean =>
  clips.length === 0 ||
  clips.every(
    (c) =>
      TERMINAL_STATES.has(c.overallState) ||
      (["MONTAGE", "FULL_MATCH_VOD"].includes(c.contentType ?? "") &&
        !c.montageId),
  );

export const useMyInTransitPolling = () => {
  const { allstarUser } = useUser();
  const client = useApolloClient();
  const [inFlightClips, setInFlightClips] = useRecoilState(InFlightClipsAtom);
  const [pollingActive, setPollingActive] = useRecoilState(PollingActiveAtom);
  const [dismissed, setDismissed] = useRecoilState(DismissedTerminalsAtom);
  const hasBootedRef = useRef(false);
  const pulseKick = useRecoilValue(PulseKickAtom);

  useEffect(() => {
    const now = Date.now();
    const pruned: Record<string, number> = {};
    let changed = false;
    for (const [key, ts] of Object.entries(dismissed)) {
      if (now - ts < DISMISSED_TTL_MS) {
        pruned[key] = ts;
      } else {
        changed = true;
      }
    }
    if (changed) setDismissed(pruned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { startPolling, stopPolling, refetch } =
    useQuery<MyInTransitClipsQueryData>(MY_IN_TRANSIT_CLIPS, {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip: !allstarUser.loggedIn,
      onCompleted: (data) => {
        const clips = data?.myInTransitClips ?? [];
        setInFlightClips(clips);

        if (isAllTerminal(clips)) {
          stopPolling();
          setPollingActive(false);
        }
      },
      onError: () => {
        stopPolling();
        setPollingActive(false);
      },
    });

  useEffect(() => {
    if (!allstarUser.loggedIn || hasBootedRef.current) return;
    hasBootedRef.current = true;

    refetch()
      .then(({ data }) => {
        const clips = data?.myInTransitClips ?? [];
        if (!isAllTerminal(clips)) {
          startPolling(POLL_INTERVAL);
          setPollingActive(true);
        }
      })
      .catch(() => {});
  }, [allstarUser.loggedIn, refetch, startPolling, setPollingActive]);

  const pulseKickRef = useRef(pulseKick);
  useEffect(() => {
    if (pulseKick === pulseKickRef.current) return;
    pulseKickRef.current = pulseKick;
    if (!allstarUser.loggedIn) return;
    refetch().catch(() => {});
    startPolling(POLL_INTERVAL);
    setPollingActive(true);
  }, [
    pulseKick,
    allstarUser.loggedIn,
    refetch,
    startPolling,
    setPollingActive,
  ]);

  const kickPulsePolling = useCallback(() => {
    if (!allstarUser.loggedIn) return;
    refetch().catch(() => {});
    startPolling(POLL_INTERVAL);
    setPollingActive(true);
  }, [allstarUser.loggedIn, refetch, startPolling, setPollingActive]);

  const dismissTerminal = useCallback(
    (trackIdOrMontageId: string) => {
      setDismissed((prev) => ({
        ...prev,
        [trackIdOrMontageId]: Date.now(),
      }));
      client.cache.evict({
        id: client.cache.identify({
          __typename: "ClipStatusTimeline",
          trackId: trackIdOrMontageId,
        }),
      });
      client.cache.gc();
    },
    [setDismissed, client],
  );

  return {
    inFlightClips,
    pollingActive,
    kickPulsePolling,
    dismissTerminal,
    dismissed,
  };
};
